import type { PostcodeTerritory } from '@valutten/shared-schemas';

export type Bounds = [number, number, number, number];
export interface PostcodeArea { postcode: string; path: string; bounds: Bounds }
export interface MapPlace { name: string; state: string; kind: string; bounds: Bounds; groupLevel?: string; postcodes?: string[] }
export interface TerritoryMapData { version: string; areas: PostcodeArea[]; places: MapPlace[] }
export interface TerritoryOffice { id: string; name: string; isHeadOffice?: boolean }
export const AUSTRALIA_BOUNDS: Bounds = [83000, 9000, 147000, 44500];

export function fitBounds(bounds: Bounds): Bounds {
  const cx = (bounds[0] + bounds[2]) / 2, cy = (bounds[1] + bounds[3]) / 2;
  const width = Math.max(bounds[2] - bounds[0], (bounds[3] - bounds[1]) * 1.5, 40) * 1.1;
  return [cx - width / 2, cy - width / 3, cx + width / 2, cy + width / 3];
}
export function overlaps(a: Bounds, b: Bounds): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}
export function centreIn(area: Bounds, view: Bounds): boolean {
  const x = (area[0] + area[2]) / 2, y = (area[1] + area[3]) / 2;
  return x >= view[0] && x <= view[2] && y >= view[1] && y <= view[3];
}
export function zoomBounds(view: Bounds, factor: number): Bounds {
  const width = view[2] - view[0];
  const next = Math.min(90000, Math.max(20, width * factor));
  const cx = (view[0] + view[2]) / 2, cy = (view[1] + view[3]) / 2;
  return [cx - next / 2, cy - next / 3, cx + next / 2, cy + next / 3];
}

/** Move is explicit; bulk selection can keep other offices' ownership. */
export function assignPostcodes(
  territories: readonly PostcodeTerritory[], officeId: string, postcodes: readonly string[], move = false,
): PostcodeTerritory[] {
  const ownedElsewhere = new Set(territories.filter((t) => t.officeId !== officeId).flatMap((t) => t.postcodes));
  const selected = new Set(postcodes.filter((p) => move || !ownedElsewhere.has(p)));
  const next = territories.map((t) => ({ ...t, postcodes: t.officeId === officeId
    ? [...new Set([...t.postcodes, ...selected])].sort()
    : t.postcodes.filter((p) => !move || !selected.has(p)) }));
  if (!next.some((t) => t.officeId === officeId)) next.push({ officeId, postcodes: [...selected].sort() });
  return next.filter((t) => t.postcodes.length > 0).sort((a,b) => a.officeId.localeCompare(b.officeId));
}

export function parseTerritoryMapData(raw: unknown): TerritoryMapData {
  const validBounds = (b: unknown): b is Bounds => Array.isArray(b) && b.length === 4
    && b.every((x) => typeof x === 'number' && Number.isFinite(x)) && b[0] < b[2] && b[1] < b[3];
  if (!raw || typeof raw !== 'object') throw new Error('Map data is invalid.');
  const d = raw as Partial<TerritoryMapData>;
  if (d.version !== 'abs-asgs-2021-v1' || !Array.isArray(d.areas) || !d.areas.length
    || !d.areas.every((a) => a && /^\d{4}$/.test(a.postcode) && typeof a.path === 'string' && a.path.length > 0 && validBounds(a.bounds))
    || !Array.isArray(d.places) || !d.places.every((p) => p && typeof p.name === 'string' && typeof p.state === 'string' && typeof p.kind === 'string' && validBounds(p.bounds) && (p.postcodes === undefined || (Array.isArray(p.postcodes) && p.postcodes.every((code) => d.areas!.some((a) => a.postcode === code)))))
    || new Set(d.areas.map((a) => a.postcode)).size !== d.areas.length) throw new Error('Map data is incomplete or invalid.');
  return d as TerritoryMapData;
}

/** Zoom around a point expressed as a fraction of the map viewport. */
export function zoomAt(view: Bounds, factor: number, x: number, y: number): Bounds {
  const width = view[2] - view[0], height = view[3] - view[1];
  const nextWidth = Math.min(90000, Math.max(20, width * factor));
  const scale = nextWidth / width;
  const left = view[0] + width * x * (1 - scale);
  const top = view[1] + height * y * (1 - scale);
  return [left, top, left + nextWidth, top + height * scale];
}
